/**
 * ADM-16 班级列表页（class-management:SC-01/02/05/08/09/10/28/35/36 前端）。
 *
 * 严格对照原型 prototype/admin/src/pages/classes/ClassListPage.tsx：
 * 筛选区（校区/SKU/状态 Select）+ 班级表格（名称 <a> 点击开详情、当前人数/容量列、
 * 状态标签）；操作列「结课 + 删除」Popconfirm（非终态时显示，删除按 ADM-16 DELETE
 * 权限显隐，软删除 -> ARCHIVED 后列表默认不再展示）；顶部「新增」按钮打开编辑对话框；
 * nextSessionTime 一期占位「-」（5-4 回填）。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import {
  classesApi,
  type ClassListItem,
  type ClassStatus,
} from '../../api/classes.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { courseSkuApi, type CourseSkuItem } from '../../api/course-skus.api';
import { ApiError } from '../../api/transport';
import { useHasPermission } from '../../auth/HasPermission';
import { useDraggableModal } from '../../components/useDraggableModal';
import { EllipsisLink } from '../../components/EllipsisCell';
import { ClassStatusTag } from './ClassStatusTag';
import { ClassDetailDrawer } from './ClassDetailDrawer';
import { ClassEditModal } from './ClassEditModal';

const { Title } = Typography;

/** 班级状态选项（原型筛选区：草稿/进行中/已结课等）。 */
const STATUS_OPTIONS: { value: ClassStatus; label: string }[] = [
  { value: "DRAFT", label: "草稿" },
  { value: "RECRUITING", label: "招生中" },
  { value: "ACTIVE", label: "进行中" },
  { value: "COMPLETED", label: "已结课" },
];

/** 提取错误信息。 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function ClassListPage() {
  // 删除按钮显隐仅依据服务端动作权限快照（ADM-16 × DELETE），越权直调由后端 403 兜底
  const canDelete = useHasPermission('ADM-16', 'DELETE');
  const canCreate = useHasPermission('ADM-16', 'CREATE');
  const canManageClass = useHasPermission('ADM-16', 'EDIT');
  const modal = useDraggableModal();
  const [data, setData] = useState<ClassListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState<{
    campusId?: string;
    skuId?: string;
    status?: ClassStatus;
  }>({});
  const [campusOptions, setCampusOptions] = useState<CampusItem[]>([]);
  const [skuOptions, setSkuOptions] = useState<CourseSkuItem[]>([]);
  const [activeClassId, setActiveClassId] = useState<string | null>(null);
  const [editing, setEditing] = useState<"new" | ClassListItem | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await classesApi.listClasses({
        ...filters,
        // 软删除（ARCHIVED）班级默认不展示；COMPLETED 保留可见（历史口径）
        excludeArchived: true,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    // 筛选下拉选项（校区 + SKU）
    void (async () => {
      try {
        const [campuses, skus] = await Promise.all([
          orgCampusApi.listCampuses({ page: 1, pageSize: 100 }),
          courseSkuApi.listCourseSkus({ page: 1, pageSize: 100 }),
        ]);
        setCampusOptions(campuses.items);
        setSkuOptions(skus.items);
      } catch {
        // 选项加载失败不阻塞列表
      }
    })();
  }, []);

  /** 结课（Popconfirm 二次确认）。 */
  const handleComplete = async (record: ClassListItem) => {
    try {
      await classesApi.completeClass(record.id);
      message.success("已结课");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  /**
   * 删除（软删除 -> ARCHIVED；Popconfirm 二次确认）。
   *
   * 被引用阻断时按稳定错误码分支引导（后端文案调整不影响提示）；
   * activeCount 不做前端预判禁用——三重阻断规则以后端 409 为唯一事实源。
   */
  const handleDelete = async (record: ClassListItem) => {
    try {
      await classesApi.deleteClass(record.id);
      message.success("已删除");
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError) {
        const blockHints: Record<string, string> = {
          "classes.class_has_active_students": `班级「${record.name}」仍有在读学生，请先移出或转班后再删除。`,
          "classes.class_has_active_schedules":
            "该班级仍有进行中的排课规则，请先在排课工作台结束排课后再删除。",
          "classes.class_has_scheduled_sessions":
            "该班级仍有未上完的未来课次，请先取消或完结课次后再删除。",
          "classes.completed_class_not_deletable":
            "已结课班级承载历史记录，不可删除。",
        };
        const hint = blockHints[err.code];
        if (hint) {
          modal.warning({ title: "无法删除", content: hint });
          return;
        }
      }
      message.error(errorMessage(err));
    }
  };

  const { columns, components } = useResizableColumns<ClassListItem>([
    {
      title: "班级名称",
      dataIndex: "name",
      width: 180,
      ellipsis: true,
      render: (v: string, r) => (
        <EllipsisLink value={v} onClick={() => setActiveClassId(r.id)} />
      ),
    },
    { title: "校区", dataIndex: "campusName", width: 120 },
    { title: "SKU", dataIndex: "skuName", width: 160 },
    { title: "教师", dataIndex: "teacherName", width: 100 },
    { title: "默认教室", dataIndex: "defaultClassroomName", width: 120 },
    {
      title: "当前人数/容量",
      dataIndex: "activeCount",
      width: 120,
      render: (v: number, r) => `${v}/${r.capacity}`,
    },
    { title: "开始日期", dataIndex: "startDate", width: 110 },
    { title: "结束日期", dataIndex: "endDate", width: 110 },
    {
      title: "下次课时间",
      dataIndex: "nextSessionTime",
      width: 150,
      render: (v: string | null) => v ?? "-",
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (v: ClassStatus) => <ClassStatusTag status={v} />,
    },
    {
      title: "操作",
      fixed: "right",
      width: 130,
      render: (_, record) =>
        record.status !== "COMPLETED" && record.status !== "ARCHIVED" ? (
          <Space>
            {canManageClass && (
              <Popconfirm
                title="确认结束该班级？结束后不再生成新课次"
                onConfirm={() => void handleComplete(record)}
              >
                <a style={{ color: "#d4380d" }}>结课</a>
              </Popconfirm>
            )}
            {canDelete && (
              <Popconfirm
                title="确认删除该班级？"
                description="删除后列表不再展示，且不可恢复"
                okText="删除"
                okButtonProps={{ danger: true }}
                onConfirm={() => void handleDelete(record)}
              >
                <a style={{ color: "#d4380d" }}>删除</a>
              </Popconfirm>
            )}
          </Space>
        ) : null,
    },
  ]);

  return (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Title level={4} style={{ margin: 0 }}>
          班级管理
        </Title>
        <Typography.Text type="secondary">
          查看班级容量、教师和排课情况，进入详情可管理成员和历史变更。
        </Typography.Text>

        <Space wrap>
          <Select
            allowClear
            placeholder="校区"
            style={{ width: 180 }}
            value={filters.campusId}
            options={campusOptions.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, campusId: v }));
            }}
          />
          <Select
            allowClear
            placeholder="课程SKU"
            style={{ width: 200 }}
            value={filters.skuId}
            options={skuOptions.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, skuId: v }));
            }}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 140 }}
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, status: v }));
            }}
          />
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing("new")}
            >
              新增
            </Button>
          )}
        </Space>

        <Table
          scroll={{ x: "max-content" }}
          rowKey="id"
          dataSource={data}
          loading={loading}
          columns={columns}
          components={components}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
            onChange: (p) => setPage(p),
          }}
        />
      </Space>

      <ClassDetailDrawer
        classId={activeClassId}
        onClose={() => setActiveClassId(null)}
        onChanged={() => void fetchList()}
      />

      {editing && (
        <ClassEditModal
          editing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void fetchList();
          }}
        />
      )}
    </Card>
  );
}
